import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { toast } from '@/hooks/use-toast';
import i18n from '@/i18n';
import { authAPI } from '@/lib/api/auth';
import {
  getApiErrorDetail,
  isAuthLoginUrl,
  isAuthRefreshUrl,
  isExpectedLoginResponse,
  isExpectedRefreshResponse,
} from '@/lib/api/error-policy';
import logger from '@/lib/utils/logger';
import { createUuid } from '@/lib/utils/uuid';

// Resolve API base URL with robust HTTPS enforcement and env override
const resolvedApiBaseUrl = (() => {
  const rawEnvUrl: unknown = import.meta.env.VITE_API_URL;
  const envUrl = typeof rawEnvUrl === 'string' ? rawEnvUrl : undefined;

  // Priority:
  // 1) Explicit env VITE_API_URL if provided (works for api subdomain setups)
  // 2) Same-origin default (reverse-proxied /api/v1 in local and production stacks)

  const configuredEnvUrl = envUrl?.trim();
  let url = configuredEnvUrl && configuredEnvUrl.length > 0 ? configuredEnvUrl : '/api/v1';

  // Normalize: remove trailing slashes
  url = url.replace(/\/+$/, '');

  // If current page is HTTPS but URL is HTTP, upgrade to HTTPS to avoid mixed content
  if (window.location.protocol === 'https:' && url.startsWith('http://')) {
    url = `https://${url.substring('http://'.length)}`;
  }

  return url;
})();

const API_BASE_URL = resolvedApiBaseUrl;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add CSRF header (cookie-based auth)
apiClient.interceptors.request.use(
  (config) => {
    // Idempotency-Key automatisch setzen für mutierende Requests
    const method = (config.method ?? 'GET').toUpperCase();
    const isMutating = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    if (isMutating && !config.headers.has('Idempotency-Key')) {
      config.headers.set('Idempotency-Key', createUuid());
    }
    // Attach CSRF token from cookie if present
    const csrfMatch = /(?:^|; )csrf_token=([^;]+)/.exec(document.cookie);
    if (csrfMatch?.[1]) {
      config.headers.set('X-CSRF-Token', decodeURIComponent(csrfMatch[1]));
    }

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }

    return config;
  },
  (error: unknown) => {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

let lastNetworkErrorAt = 0;

export type AuthRetryRequestConfig = AxiosRequestConfig & {
  authRetryAttempted?: true;
};

export function isAuthRetryAttempted(config: AxiosRequestConfig): boolean {
  return (config as AuthRetryRequestConfig).authRetryAttempted === true;
}

export function markAuthRetryAttempted(config: AxiosRequestConfig): AuthRetryRequestConfig {
  const retryableConfig = config as AuthRetryRequestConfig;
  retryableConfig.authRetryAttempted = true;
  return retryableConfig;
}

// Response interceptor to handle refresh-and-retry before logout
apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      throw error instanceof Error ? error : new Error(String(error));
    }

    const originalRequest = error.config;

    // Log/Toast mit Drosselung für Netzwerkfehler
    const url = error.config?.url;
    const isAnalytics = url?.includes('/analytics/') === true;
    // Fire-and-forget telemetry can run without a signed-in user and must never
    // trigger refresh or logout. Authenticated analytics session endpoints are
    // intentionally excluded: a 401 there must use the normal refresh-and-retry
    // flow instead of producing recurring failed session requests.
    const isAnalyticsTelemetry =
      typeof url === 'string' &&
      (/^\/analytics\/events\/(track|bulk)\b/.test(url) ||
        /^\/analytics\/errors\/log\b/.test(url) ||
        /^\/analytics\/anonymous\/(sessions|events)\b/.test(url));
    const status = error.response?.status;
    const isAuthMe = typeof url === 'string' && /\/auth\/me\b/.test(url);
    const isAuthRefresh = isAuthRefreshUrl(url);
    const isAuthLogin = isAuthLoginUrl(url);
    const isExpectedLoginFailure = isExpectedLoginResponse(url, status);
    // A refresh that returns 401/403 means the session cannot be renewed. This
    // is the normal "not signed in" state (e.g. a stale csrf marker without a
    // live refresh token), not an error to surface to the user.
    const isExpectedRefreshFailure = isExpectedRefreshResponse(url, status);

    // Public endpoints that should not trigger auth redirect on 401
    // These endpoints use optional auth - they work without authentication
    const isPublicEndpoint =
      typeof url === 'string' &&
      (/^\/characters\/?(\?|$)/.test(url) || // GET /characters/
        /^\/characters\/\d+\/?(\?|$)/.test(url) || // GET /characters/:id
        /^\/characters\/\d+\/avatar\b/.test(url) || // GET /characters/:id/avatar
        url.startsWith('/media/avatars/') || // Avatar images
        url.startsWith('/media/gallery/') || // Gallery images
        /^\/creator-space\/creators\/?(\?|$)/.test(url)); // GET /creator-space/creators

    if (error.code === 'ERR_NETWORK') {
      // Analytics blockers are expected in some browsers, but remain observable.
      if (!isAnalytics) {
        const now = Date.now();
        if (now - lastNetworkErrorAt > 15000) {
          lastNetworkErrorAt = now;
          logger.error('Network Error: Backend is not reachable', API_BASE_URL);
          toast({
            title: i18n.t('admin.errors.networkTitle', 'Netzwerkfehler'),
            description: i18n.t('admin.errors.networkUnavailable', 'Server nicht erreichbar.'),
            variant: 'destructive',
          });
        }
      } else {
        logger.warn('Analytics request blocked or unreachable', { url });
      }
    } else if (error.response?.status !== 404 && status !== 401) {
      if (!isAnalytics && !(status === 401 && isAuthMe) && !isExpectedRefreshFailure && !isExpectedLoginFailure) {
        logger.error('API Error:', error.response?.status, error.response?.data);
      }
    }

    // Bei 401 zunächst einmalig Refresh versuchen und Original-Request wiederholen,
    // aber NICHT für /auth/me, öffentliche Endpunkte oder fire-and-forget Telemetrie,
    // um Endlosschleifen und ungewollte Logout/Redirects zu vermeiden.
    if (
      !isAuthLogin &&
      !isAuthMe &&
      !isAuthRefresh &&
      !isPublicEndpoint &&
      !isAnalyticsTelemetry &&
      status === 401 &&
      originalRequest &&
      !isAuthRetryAttempted(originalRequest)
    ) {
      try {
        markAuthRetryAttempted(originalRequest);
        await authAPI.refresh();
        return await apiClient.request(originalRequest);
      } catch (refreshError: unknown) {
        logger.warn('Session refresh failed; logging out', refreshError);
        // Refresh fehlgeschlagen -> Logout/Redirect
        try {
          await authAPI.logout();
        } catch (logoutError: unknown) {
          logger.error('Server logout failed after refresh failure', logoutError);
        }
        if (!window.location.pathname.includes('/auth')) {
          window.location.href = '/auth';
        }
        throw error;
      }
    }

    // Für öffentliche Endpunkte und fire-and-forget Telemetrie: Bei 401 einfach den Fehler
    // werfen ohne Redirect. Die UI kann dann entscheiden, ob sie den Nutzer zum Login leitet;
    // Telemetrie-Fehler werden vom safeAnalyticsCall-Wrapper verschluckt.
    if ((isPublicEndpoint || isAnalyticsTelemetry) && status === 401) {
      throw error;
    }

    if (status && status >= 500) {
      if (!isAnalytics) {
        toast({
          title: i18n.t('admin.errors.serverErrorTitle', 'Serverfehler'),
          description: i18n.t('admin.errors.tryAgainLater', 'Bitte später erneut versuchen.'),
          variant: 'destructive',
        });
      }
      throw error;
    }

    if (status && status >= 400) {
      // Falsche Login-Daten sind ein erwarteter Formularzustand. Die Auth-Seite
      // zeigt ihn inline; globaler Error-Log, Toast und Refresh wären doppelt.
      if (isExpectedLoginFailure) {
        throw error;
      }

      // Keine Toasts/Logs für erwartetes 401 auf /auth/me (unauthenticated initial state)
      if (status === 401 && isAuthMe) {
        throw error;
      }

      // Ein fehlgeschlagener Session-Refresh (401/403) ist ein erwarteter
      // "nicht eingeloggt"-Zustand. Der Aufrufer (Bootstrap/Retry/WebSocket)
      // behandelt die Ablehnung selbst; hier kein Toast.
      if (isExpectedRefreshFailure) {
        throw error;
      }

      // Spezifisch: Creator-Routen erlauben 401/403 ohne lauten Toast, da die UI Preview/Overlays zeigt
      const isCreatorGet =
        typeof url === 'string' &&
        // Profil- und Statistikabruf sind GETs, Fehler hier sollen leise sein
        (/\/creator-space\/creators\/\d+\b/.test(url) ||
          /\/creator-space\/creators\/\d+\/posts\b/.test(url) ||
          /\/creator-space\/timeline\b/.test(url) ||
          /\/creator-space\/posts\/\d+\b/.test(url) ||
          /\/creator-space\/posts\/\d+\/comments\b/.test(url));

      // 401/403 auf Creator-GETs: kein Toast (UI übernimmt Preview/Hinweise)
      const method = (error.config?.method ?? 'get').toUpperCase();
      const isGet = method === 'GET';
      if (isCreatorGet && isGet && (status === 401 || status === 403)) {
        throw error;
      }

      // Spezifisch: Swipe-Routen – erwartete Zustände ohne Toast (UI zeigt Status)
      const isSwipeNext = typeof url === 'string' && /\/swipe\/next\b/.test(url);
      const isSwipeAction = typeof url === 'string' && /\/swipe\/action\b/.test(url);
      // /swipe/next: 404 (keine Profile) oder 402 (keine Swipes) -> leise behandeln
      if (isSwipeNext && (status === 404 || status === 402)) {
        throw error;
      }
      // /swipe/action: 400 (already swiped) oder 402 (keine Swipes/Superlikes) -> leise behandeln (UI übernimmt)
      if (isSwipeAction && (status === 400 || status === 402)) {
        throw error;
      }

      // Spezifisch: View-Endpoint darf 400 "Cannot record view" still sein
      const isCreatorViewPost = typeof url === 'string' && /\/creator-space\/posts\/\d+\/view\b/.test(url);
      if (isCreatorViewPost && status === 400) {
        throw error;
      }

      // Spezifisch: Extended Onboarding-Routen – 404 leise behandeln (UI zeigt nichts, wenn deaktiviert/keine Characters)
      const isExtendedOnboarding = typeof url === 'string' && /\/onboarding\/extended\b/.test(url);
      if (isExtendedOnboarding && status === 404) {
        throw error;
      }

      // Spezifisch: Chat-Session-Create 400 (z.B. "Chat access denied: open_safety_report")
      // wird von Chat.tsx leise als Lock-Banner behandelt — kein globaler Toast.
      const isChatSessionCreate = typeof url === 'string' && /\/chat\/session\/\d+\b/.test(url);
      if (isChatSessionCreate && status === 400) {
        throw error;
      }

      const detail = getApiErrorDetail(error) ?? error.message;
      if (!isAnalytics) {
        toast({ title: i18n.t('admin.errors.errorTitle', 'Fehler'), description: detail, variant: 'destructive' });
      }
      throw error;
    }

    throw error;
  }
);

apiClient.defaults.timeout = 15000;

// Helper function for API requests
export interface ApiRequestOptions<Data = unknown> {
  method?: AxiosRequestConfig['method'];
  data?: Data;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  timeout?: number;
}

export const apiRequest = async <TResponse = unknown, TData = unknown>(
  url: string,
  options: ApiRequestOptions<TData> = {}
): Promise<TResponse> => {
  const config: AxiosRequestConfig<TData> = {
    url,
    method: options.method ?? 'GET',
    timeout: options.timeout ?? 15000,
    ...(options.data === undefined ? {} : { data: options.data }),
    ...(options.headers === undefined ? {} : { headers: options.headers }),
    ...(options.params === undefined ? {} : { params: options.params }),
  };
  const response = await apiClient.request<TResponse>(config);
  return response.data;
};

export default apiClient;
